import { useState, useEffect, useMemo, useCallback } from 'react';
import SelectAllClearToggles from '../../../../components/v2/SelectAllClearToggles.jsx';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * BulkGroupDrawer — set resident group membership for several residents at once.
 *
 * Whatever is checked becomes the complete group list for every selected
 * resident, matching both the backend (sp_tlGroupResident replaces membership)
 * and the single-resident edit drawer. Groups left unchecked are removed.
 *
 * Starts with nothing checked: with several residents selected there is no
 * single "current" set to pre-fill, so the per-group counts ("in 2 of 3
 * selected") show the existing state instead. Residents that already match the
 * checked set are dropped from the payload so no pointless writes are sent.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (changes: Array<{resident_id, groupIds, oldGroupIds}>) => Promise<void>,
 *   groups: Array<{ resident_group_id: number, resident_group_name: string }>,
 *   residents: Array<object>,   // the SELECTED resident rows
 *   saving: boolean,
 * }} props
 */

const idsOf = (resident) => (resident?.resident_groups || [])
  .map((g) => g.resident_group_id)
  .filter((id) => id != null);

const sameSet = (a, b) => {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((v, i) => v === sb[i]);
};

/* lookupsReady: the group list has loaded for this campus. The page shows its table
   before it arrives; until then the list reads "Loading" (not "no groups exist") and Save is
   blocked, because saving with no groups checked clears every selected resident's groups. */
const BulkGroupDrawer = ({ isOpen, onClose, onSave, groups, residents, saving, lookupsReady = true }) => {
  const [checkedIds, setCheckedIds] = useState([]);

  // Reset on open
  useEffect(() => {
    if (isOpen) setCheckedIds([]);
  }, [isOpen]);

  const selected = residents || [];
  const sortedGroups = useMemo(
    () => [...(groups || [])].sort((a, b) =>
      (a.resident_group_name || '').localeCompare(b.resident_group_name || '', undefined, { sensitivity: 'base' })),
    [groups],
  );

  /** How many of the selected residents are already in each group */
  const membershipCounts = useMemo(() => {
    const counts = {};
    selected.forEach((r) => {
      idsOf(r).forEach((id) => { counts[id] = (counts[id] || 0) + 1; });
    });
    return counts;
  }, [selected]);

  const toggleGroup = useCallback((groupId) => {
    setCheckedIds((prev) => (prev.includes(groupId)
      ? prev.filter((id) => id !== groupId)
      : [...prev, groupId]));
  }, []);

  /** The checked set becomes every selected resident's full list; no-ops dropped. */
  const changes = useMemo(() => selected
    .map((r) => ({
      resident_id: r.resident_id,
      groupIds: [...checkedIds],
      oldGroupIds: idsOf(r),
    }))
    .filter((c) => !sameSet(c.groupIds, c.oldGroupIds)), [selected, checkedIds]);

  // Clearing every group is a legitimate action, so an empty selection still
  // counts as work — it just gets called out below.
  const hasWork = changes.length > 0;
  const isClearAll = checkedIds.length === 0 && changes.length > 0;

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!hasWork || !lookupsReady) return;
    onSave(changes);
  }, [hasWork, changes, onSave, lookupsReady]);

  const summary = (() => {
    // Before the group list arrives nothing is checked, which would read as "removes all".
    if (!lookupsReady) return 'Loading groups…';
    // Nothing to write. Worded as a statement of fact, never as "pick a group" —
    // saving zero groups is valid, so the UI must not imply one is required.
    if (changes.length === 0) {
      if (selected.length === 0) return 'No residents selected.';
      return checkedIds.length === 0
        ? 'The selected residents already have no groups — nothing to apply.'
        : 'No changes — the selected residents already have exactly these groups.';
    }
    const n = changes.length;
    const who = `${n} resident${n !== 1 ? 's' : ''}`;
    if (isClearAll) return `Warning: removes all groups from ${who}.`;
    return `Sets ${checkedIds.length} group${checkedIds.length !== 1 ? 's' : ''} on ${who}. Groups not checked will be removed.`;
  })();

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      classPrefix="rp"
      size="md"
      ariaLabel="Edit groups for selected residents"
      title="Edit Groups"
      rawBody
    >
      <form onSubmit={handleSubmit} className="rp-form">
        <div className="rp-form__body">
          <p className="rp-field__label" style={{ marginBottom: 0 }}>
            {selected.length} resident{selected.length !== 1 ? 's' : ''} selected
          </p>

          {/* Groups */}
          <div>
            <div className="sac-field-header">
              <p className="rp-field__label">Groups</p>
              {lookupsReady && sortedGroups.length > 0 ? (
                <SelectAllClearToggles
                  onSelectAll={() => setCheckedIds(sortedGroups.map((g) => g.resident_group_id))}
                  onClear={() => setCheckedIds([])}
                  allSelected={checkedIds.length === sortedGroups.length}
                  noneSelected={checkedIds.length === 0}
                />
              ) : null}
            </div>
            {!lookupsReady ? (
              <p className="rp-cell-text--muted">Loading groups…</p>
            ) : sortedGroups.length === 0 ? (
              <p className="rp-cell-text--muted">No resident groups exist for this community yet.</p>
            ) : (
              <div className="rp-group-grid">
                {sortedGroups.map((group) => {
                  const isChecked = checkedIds.includes(group.resident_group_id);
                  const count = membershipCounts[group.resident_group_id] || 0;
                  return (
                    <label
                      key={group.resident_group_id}
                      className={`rp-group-option ${isChecked ? 'is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleGroup(group.resident_group_id)}
                        className="rp-checkbox"
                      />
                      <span className="rp-group-option__text">
                        {group.resident_group_name}
                        <span style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                          {count === 0
                            ? 'none of the selected'
                            : `in ${count} of ${selected.length} selected`}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* What will happen */}
          <p className={`rp-bulk-summary${isClearAll ? ' rp-bulk-summary--danger' : ''}`}>
            {summary}
          </p>
        </div>

        <div className="rp-form__footer">
          <button type="button" onClick={onClose} className="rp-btn rp-btn--cancel">
            Cancel
          </button>
          <button type="submit" disabled={!hasWork || saving || !lookupsReady}
            title={lookupsReady ? undefined : 'Loading groups…'} className="rp-btn rp-btn--primary">
            {saving ? 'Saving...' : 'Apply'}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default BulkGroupDrawer;
